import React, { useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  writeBatch,
  getDoc,
} from "firebase/firestore";
import Papa from "papaparse";
import {
  FileUp,
  Download,
  Search,
  Mail,
  Package,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Upload,
} from "lucide-react";

function Operations() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [eduData, setEduData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [isCheckingFree, setIsCheckingFree] = useState(false);
  const recordsPerPage = 40;

  const db = getFirestore();
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetchData();
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch products from database
  const fetchProducts = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "Products"));
      const productsList = querySnapshot.docs.map(
        (doc) => doc.data().name || "Unknown Product"
      );
      // Remove duplicates and sort alphabetically
      const uniqueProducts = [...new Set(productsList)].sort();
      setProducts(uniqueProducts);
    } catch (error) {
      console.error("Error fetching products: ", error);
    }
  };

  // Helper function to normalize email addresses
  const normalizeEmail = (email) => {
    if (!email) return "";
    // Convert to lowercase and trim whitespace
    return email.toLowerCase().trim();
  };

  // This method fetches data from both Educhecker collection and Sales collection
  // to ensure we have a complete picture of products associated with each email
  const fetchData = async () => {
    setLoading(true);
    try {
      // Create a Map to store email-to-products relationships
      const emailProductMap = new Map();

      // First, fetch from Educhecker collection
      const querySnapshot = await getDocs(collection(db, "Educhecker"));

      querySnapshot.docs.forEach((doc) => {
        const docData = doc.data();
        // Normalize the email
        const eduMail = normalizeEmail(docData.eduMail || "");
        const productName = docData.productName || "";

        if (!eduMail) return; // Skip if no email

        if (emailProductMap.has(eduMail)) {
          // If we already have this email, add the product to its products array
          const existingEntry = emailProductMap.get(eduMail);
          if (
            productName &&
            !existingEntry.productNames.includes(productName)
          ) {
            existingEntry.productNames.push(productName);
          }
        } else {
          // First time seeing this email
          emailProductMap.set(eduMail, {
            id: doc.id,
            eduMail: eduMail, // Store normalized email
            originalEmail: docData.eduMail || "", // Store original email format for display
            productNames: productName ? [productName] : [],
          });
        }
      });

      // Second, fetch from Sales to get any additional product-email associations
      const salesSnapshot = await getDocs(collection(db, "Sales"));

      salesSnapshot.docs.forEach((doc) => {
        const saleData = doc.data();
        const eduMail = normalizeEmail(saleData.emailEdu || "");
        const productName = saleData.productName || "";

        if (!eduMail || !productName) return; // Skip if missing data

        if (emailProductMap.has(eduMail)) {
          // Add product to existing email entry
          const existingEntry = emailProductMap.get(eduMail);
          if (!existingEntry.productNames.includes(productName)) {
            existingEntry.productNames.push(productName);
          }
        } else {
          // Create new email entry
          emailProductMap.set(eduMail, {
            id: eduMail,
            eduMail: eduMail,
            originalEmail: saleData.emailEdu || "",
            productNames: [productName],
          });
        }
      });

      // Convert Map to array
      const data = Array.from(emailProductMap.values());

      // Sort the data first by email
      data.sort((a, b) => a.eduMail.localeCompare(b.eduMail));

      setEduData(data);
      setTotalPages(Math.ceil(data.length / recordsPerPage));
    } catch (error) {
      console.error("Error fetching data: ", error);
      setError("Failed to fetch data. Please try again.");
    }
    setLoading(false);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError("");
    setSuccess("");
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a CSV file first.");
      return;
    }

    setUploading(true);
    setError("");
    setSuccess("");

    Papa.parse(file, {
      complete: async (results) => {
        try {
          const batch = writeBatch(db);
          let addedCount = 0;

          for (let i = 0; i < results.data.length; i++) {
            const [eduMail, productName] = results.data[i];
            if (eduMail && productName) {
              // Normalize the email address before saving
              const normalizedEmail = normalizeEmail(eduMail);

              // Use the normalized email as the document ID
              const eduDocRef = doc(db, "Educhecker", normalizedEmail);

              // Check if the document already exists
              const eduDoc = await getDoc(eduDocRef);

              if (eduDoc.exists()) {
                // Update existing document - add product to productNames array if not exists
                const existingData = eduDoc.data();
                const productNames = existingData.productNames || [];

                if (!productNames.includes(productName)) {
                  batch.update(eduDocRef, {
                    productNames: [...productNames, productName],
                    eduMail: normalizedEmail,
                    lastUpdated: new Date(),
                  });
                }
              } else {
                // Create new document with productNames array
                batch.set(eduDocRef, {
                  eduMail: normalizedEmail,
                  productNames: [productName],
                  createdAt: new Date(),
                });
              }

              addedCount++;
            }
          }

          await batch.commit();
          setSuccess(
            `Successfully added ${addedCount} records to the database.`
          );
          fetchData();
        } catch (error) {
          console.error("Error uploading CSV data: ", error);
          setError("Failed to upload CSV data. Please try again.");
        } finally {
          setUploading(false);
        }
      },
      error: (error) => {
        console.error("Error parsing CSV: ", error);
        setError("Failed to parse CSV file. Please check the file format.");
        setUploading(false);
      },
    });
  };

  const downloadSampleCsv = () => {
    const csvContent =
      "EDU Email,Product Name\nstudent@university.edu,Product A\nanother@college.edu,Product B";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "sample_edu_products.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const filteredData = eduData.filter((item) => {
    // Normalize the search term to match the normalized stored emails
    const normalizedSearchTerm = normalizeEmail(searchTerm);
    const emailMatch = item.eduMail.includes(normalizedSearchTerm);

    let productMatch = true;
    if (selectedProduct) {
      if (isCheckingFree) {
        // When checking for free emails, we want to show emails that DON'T have the selected product
        productMatch = !item.productNames.includes(selectedProduct);
      } else {
        // Normal mode - show emails that have the selected product
        productMatch = item.productNames.includes(selectedProduct);
      }
    }

    return emailMatch && productMatch;
  });

  // Check if an email is available for a specific product
  // eslint-disable-next-line no-unused-vars
  const checkEmailAvailability = (email, product) => {
    const normalizedEmail = normalizeEmail(email);
    const matchingItem = eduData.find(
      (item) => item.eduMail === normalizedEmail
    );

    if (!matchingItem) {
      return { available: true, message: "Email not found, it's available!" };
    }

    return {
      available: !matchingItem.productNames.includes(product),
      message: matchingItem.productNames.includes(product)
        ? `Email already used for ${product}`
        : `Email is available for ${product}`,
      products: matchingItem.productNames,
    };
  };

  const paginatedData = filteredData.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  // Update total pages when filtered data changes
  useEffect(() => {
    const newTotalPages = Math.ceil(filteredData.length / recordsPerPage);
    setTotalPages(newTotalPages);
    
    // Only reset to page 1 if current page is beyond the new total pages
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredData.length, recordsPerPage, currentPage]);

  // Separate effect to reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedProduct, isCheckingFree]);

  const styles = {
    container: {
      maxWidth: "1000px",
      margin: "0 auto",
      padding: "24px",
      backgroundColor: "#f8fafc",
      borderRadius: "8px",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    },
    header: {
      color: "#1e293b",
      fontSize: "24px",
      fontWeight: "600",
      marginBottom: "24px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    buttonContainer: {
      display: "flex",
      gap: "12px",
      marginBottom: "24px",
      flexWrap: "wrap",
    },
    actionButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 16px",
      backgroundColor: "#ffffff",
      color: "#1e293b",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      transition: "all 0.2s ease",
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    },
    uploadButton: {
      backgroundColor: "#3b82f6",
      color: "white",
      border: "none",
      "&:hover": {
        backgroundColor: "#2563eb",
      },
    },
    searchContainer: {
      position: "relative",
      marginBottom: "24px",
    },
    searchIcon: {
      position: "absolute",
      left: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#64748b",
    },
    searchInput: {
      width: "100%",
      padding: "12px 12px 12px 40px",
      fontSize: "14px",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      transition: "all 0.2s ease",
      backgroundColor: "white",
      "&:focus": {
        outline: "none",
        borderColor: "#3b82f6",
        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
      },
    },
    table: {
      width: "100%",
      backgroundColor: "white",
      borderRadius: "8px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    },
    th: {
      backgroundColor: "#f8fafc",
      padding: "12px 16px",
      textAlign: "left",
      color: "#64748b",
      fontSize: "12px",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    td: {
      padding: "12px 16px",
      borderBottom: "1px solid #e2e8f0",
      color: "#1e293b",
      fontSize: "14px",
    },
    pagination: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
      marginTop: "24px",
    },
    pageButton: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      padding: "8px 16px",
      backgroundColor: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      transition: "all 0.2s ease",
      "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
      },
    },
    fileInput: {
      display: "none",
    },
    notification: {
      padding: "12px 16px",
      borderRadius: "6px",
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    errorNotification: {
      backgroundColor: "#fee2e2",
      color: "#dc2626",
    },
    successNotification: {
      backgroundColor: "#d1fae5",
      color: "#059669",
    },
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>
        <Package size={24} />
        EDU Email and Product Management
      </h2>

      {error && (
        <div style={{ ...styles.notification, ...styles.errorNotification }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}
      {success && (
        <div style={{ ...styles.notification, ...styles.successNotification }}>
          <CheckCircle size={18} />
          {success}
        </div>
      )}

      <div style={styles.buttonContainer}>
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{ ...styles.actionButton, ...styles.uploadButton }}
          disabled={uploading}
        >
          <FileUp size={18} />
          Upload CSV
        </button>
        <button
          onClick={handleUpload}
          style={{ ...styles.actionButton, ...styles.uploadButton }}
          disabled={!file || uploading}
        >
          <Upload size={18} />
          Process Upload
        </button>
        <button onClick={downloadSampleCsv} style={styles.actionButton}>
          <Download size={18} />
          Download Sample CSV
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".csv"
          style={styles.fileInput}
        />
      </div>

      <div style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
        <div style={{ ...styles.searchContainer, marginBottom: "0", flex: 1 }}>
          <Search size={18} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by EDU Email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <div style={{ flex: 1 }}>
          <select
            value={selectedProduct}
            onChange={(e) => setSelectedProduct(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              fontSize: "14px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              backgroundColor: "white",
            }}
          >
            <option value="">All Products</option>
            {products.map((product, index) => (
              <option key={index} value={product}>
                {product}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={isCheckingFree}
            onChange={() => setIsCheckingFree(!isCheckingFree)}
            style={{ marginRight: "8px", width: "16px", height: "16px" }}
          />
          Check for Free Emails
        </label>
      </div>

      {/* Display total free emails when checkbox is checked */}
      {isCheckingFree && (
        <div
          style={{
            backgroundColor: "#f0f9ff",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "16px",
            border: "1px solid #bae6fd",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <Mail style={{ color: "#0369a1" }} size={20} />
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                margin: 0,
                color: "#0369a1",
              }}
            >
              Free Emails Summary
            </h3>
          </div>
          
          {selectedProduct ? (
            <div>
              <p style={{ fontSize: "14px", marginBottom: "8px", margin: 0 }}>
                <span style={{ fontWeight: "500" }}>Total free emails available for "{selectedProduct}":</span>{" "}
                <span style={{ 
                  color: "#059669", 
                  fontWeight: "bold", 
                  fontSize: "16px" 
                }}>
                  {eduData.filter(item => !item.productNames.includes(selectedProduct)).length}
                </span>
              </p>
              <p style={{ fontSize: "12px", color: "#64748b", margin: 0 }}>
                These emails have not been used for {selectedProduct} and are available for new campaigns.
              </p>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: "14px", marginBottom: "8px", margin: 0 }}>
                <span style={{ fontWeight: "500" }}>Total emails in database:</span>{" "}
                <span style={{ 
                  color: "#059669", 
                  fontWeight: "bold", 
                  fontSize: "16px" 
                }}>
                  {eduData.length}
                </span>
              </p>
              <p style={{ fontSize: "12px", color: "#64748b", margin: 0 }}>
                Select a product above to see how many free emails are available for that specific product.
              </p>
            </div>
          )}
        </div>
      )}

      {selectedProduct && (
        <div
          style={{
            backgroundColor: "#f0f9ff",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
            border: "1px solid #bae6fd",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "8px",
              color: "#0369a1",
            }}
          >
            Product Availability: {selectedProduct}
          </h3>
          <div>
            <p style={{ fontSize: "14px", marginBottom: "8px" }}>
              <span style={{ fontWeight: "500" }}>Total emails available:</span>{" "}
              {
                eduData.filter(
                  (item) => !item.productNames.includes(selectedProduct)
                ).length
              }
            </p>
            <p style={{ fontSize: "14px" }}>
              <span style={{ fontWeight: "500" }}>Status:</span>{" "}
              {eduData.filter(
                (item) => !item.productNames.includes(selectedProduct)
              ).length > 0 ? (
                <span style={{ color: "#059669" }}>Available</span>
              ) : (
                <span style={{ color: "#dc2626" }}>
                  No free emails available
                </span>
              )}
            </p>
          </div>

          {/* "Check Email Availability" section */}
          <div
            style={{
              marginTop: "16px",
              borderTop: "1px solid #bae6fd",
              paddingTop: "16px",
            }}
          >
            <h4
              style={{
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              Check Email Availability
            </h4>
            <p
              style={{
                fontSize: "12px",
                marginBottom: "8px",
                color: "#64748b",
              }}
            >
              Enter an email to check if it's available for {selectedProduct}
            </p>

            <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
              <input
                type="text"
                placeholder="Enter EDU email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  fontSize: "14px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "4px",
                }}
              />
            </div>

            {searchTerm && (
              <div
                style={{
                  padding: "8px",
                  backgroundColor: "#e0f2fe",
                  borderRadius: "4px",
                }}
              >
                {filteredData.length > 0 ? (
                  filteredData.map((item) => (
                    <div key={item.id} style={{ marginBottom: "4px" }}>
                      <p>
                        <strong>{item.originalEmail || item.eduMail}</strong>
                      </p>
                      <p style={{ fontSize: "12px" }}>
                        {item.productNames.includes(selectedProduct) ? (
                          <span style={{ color: "#dc2626" }}>
                            Already used for {selectedProduct}
                          </span>
                        ) : (
                          <span style={{ color: "#059669" }}>
                            Available for {selectedProduct}
                          </span>
                        )}
                      </p>
                      <p style={{ fontSize: "12px", color: "#64748b" }}>
                        Products: {item.productNames.join(", ")}
                      </p>
                    </div>
                  ))
                ) : (
                  <p style={{ fontSize: "14px", color: "#059669" }}>
                    Email not found! It's available for {selectedProduct}.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Data display status */}
      <div
        style={{
          backgroundColor: "#f8fafc",
          padding: "12px 16px",
          borderRadius: "8px",
          marginBottom: "16px",
          border: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <p style={{ fontSize: "14px", margin: 0, color: "#374151" }}>
            {isCheckingFree && selectedProduct ? (
              <>
                Showing <strong>{filteredData.length}</strong> free emails available for <strong>{selectedProduct}</strong>
              </>
            ) : selectedProduct && !isCheckingFree ? (
              <>
                Showing <strong>{filteredData.length}</strong> emails already used for <strong>{selectedProduct}</strong>
              </>
            ) : searchTerm ? (
              <>
                Showing <strong>{filteredData.length}</strong> emails matching "<strong>{searchTerm}</strong>"
              </>
            ) : (
              <>
                Showing all <strong>{filteredData.length}</strong> emails in database
              </>
            )}
          </p>
        </div>
        
        {(isCheckingFree || selectedProduct || searchTerm) && (
          <button
            onClick={() => {
              setIsCheckingFree(false);
              setSelectedProduct("");
              setSearchTerm("");
              setCurrentPage(1);
            }}
            style={{
              fontSize: "12px",
              color: "#6b7280",
              background: "none",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              padding: "4px 8px",
              cursor: "pointer",
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <Mail size={14} />
                EDU Email
              </div>
            </th>
            <th style={styles.th}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <Package size={14} />
                Product Names
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((item) => (
            <tr key={item.id}>
              <td style={styles.td}>{item.originalEmail || item.eduMail}</td>
              <td style={styles.td}>
                {item.productNames && item.productNames.length > 0 ? (
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}
                  >
                    {item.productNames.map((product, index) => (
                      <span
                        key={index}
                        style={{
                          backgroundColor:
                            product === selectedProduct ? "#f0f9ff" : "#f1f5f9",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight:
                            product === selectedProduct ? "600" : "normal",
                          border:
                            product === selectedProduct
                              ? "1px solid #bae6fd"
                              : "none",
                        }}
                      >
                        {product}
                      </span>
                    ))}
                  </div>
                ) : (
                  "No products"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={styles.pagination}>
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={styles.pageButton}
        >
          <ChevronLeft size={16} />
          Previous
        </button>
        <span style={{ color: "#64748b", fontSize: "14px" }}>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
          style={styles.pageButton}
        >
          Next
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

export default Operations;
